import type { RefObject } from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';

import type { TrackingProps } from 'js/lib/retracked';
import Retracked from 'js/lib/retracked';
import { useParams } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { RouteNames } from 'pages/teach-course/constants';

import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';

export const useOutsideClickHandler = (ref: RefObject<HTMLElement>, callback: (event: MouseEvent) => void) => {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        callback(e);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [callback, ref]);
};

export const useKeyDownHandler = (callback: (event: KeyboardEvent) => void) => {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      callback(e);
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [callback]);
};

export function useDebouncedValue<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Confirm browser exit. Calls `onTrigger` when `beforeunload` is triggered and confirmation is enabled.
 * Note: it uses `beforeunload` which is not reliable on mobile.
 *
 * @param conf Settings
 */
export const useConfirmBrowserExit = ({
  defaultEnabled = false,
  onTrigger,
  message = 'Confirm leave page',
}: {
  defaultEnabled?: boolean;
  onTrigger?: () => Promise<void>;
  message?: string;
}) => {
  const [enabled, setEnabled] = useState<boolean>(defaultEnabled);

  useEffect(() => {
    function eventHandler(e: BeforeUnloadEvent) {
      if (enabled) {
        onTrigger?.().then(() => {
          setEnabled(false);
        });
        e.preventDefault();
        // Gecko + IE
        e.returnValue = message;
      }
      // Safari, Chrome, and other WebKit-derived browsers
      return enabled ? message : undefined;
    }

    window.addEventListener('beforeunload', eventHandler);

    return () => {
      window.removeEventListener('beforeunload', eventHandler);
    };
  }, [message, enabled, onTrigger]);

  return { setEnabled };
};

/**
 * Hook to track any Retracked events. Uses specific tracking data
 * on all events
 *
 * @returns () => (props: TrackingProps) => void
 */
export const useEventTracker = () => {
  const { pageData } = useContext(SGAPageContext);
  const routeParams = useParams();
  const graderId = user.get().id;

  return useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (props: TrackingProps) => {
      Retracked.trackComponent(
        {
          namespace: {
            app: 'course',
            page: RouteNames.ASSIGNMENT_QUICK_GRADER,
          },
        },
        Object.assign({}, props.trackingData, {
          courseId: pageData.courseId,
          courseBranchId: pageData.courseBranchId,
          sessionId: pageData.sessionItem?.sessionId,
          itemId: routeParams.itemId,
          submitterId: routeParams.id,
          groupId: routeParams.contextId,
          graderId,
        }),
        props.trackingName,
        props.action
      );
    },
    [
      graderId,
      pageData.courseBranchId,
      pageData.courseId,
      pageData.sessionItem?.sessionId,
      routeParams.contextId,
      routeParams.id,
      routeParams.itemId,
    ]
  );
};
